import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import AddressInputModal from "./AddressInputModal";
import type {
  InlineContractRendererRef,
  InlineContractValues,
} from "./InlineContractRenderer";
import type {
  ContractProgress,
  ContractProgressItem,
} from "./RichContractRenderer";
import { useDragPan } from "../../hooks/common/useDragPan";
import { CONTRACT_PAGE_CSS } from "@/business/contract/ContractRichEditor";
import { renderPageBackground } from "@/business/contract/editor/docBackgroundRenderer";

const DEFAULT_PAGE_WIDTH = 794;
const DEFAULT_PAGE_HEIGHT = 1123;
const MIN_PAGE_SCALE = 0.35;
const MAX_PAGE_SCALE = 1.6;
const ZOOM_STEP = 0.15;
const ADDRESS_KEYS = new Set(["guardianAddress", "address", "addr"]);

type PinchState = {
  startDistance: number;
  startScale: number;
};

type PlacementItem = {
  kind: "variable" | "field";
  pageNo: number;
  key: string;
  label?: string;
  fieldType?: string | null;
  required?: boolean;
  fieldOptions?: CheckboxFieldOptions | null;
  x: number;
  y: number;
  width: number;
  height: number;
};

type CheckboxDisplayMode = "MARK_ONLY" | "BOX_AND_MARK";
type CheckboxSelectionMode = "SINGLE" | "ONE_OF" | "MULTI";

type CheckboxFieldOptions = {
  checkboxDisplay?: CheckboxDisplayMode;
  checkboxMode?: CheckboxSelectionMode;
  groupKey?: string;
  optionValue?: string;
  textMode?: "SINGLE_LINE" | "MULTILINE";
};

type PlacementManifest = {
  pageWidth?: number;
  pageHeight?: number;
  items?: PlacementItem[];
};

interface Props {
  pageUrls: string[];
  placementManifest?: string | null;
  variables?: Record<string, string | null | undefined> | null;
  initialAddress?: string;
  sealImageUrl?: string | null;
  sealImageUrls?: Record<string, string | null | undefined> | null;
  signatureImageUrl?: string | null;
  readOnly?: boolean;
  className?: string;
  /** 렌더 이미지가 없을 때 배경으로 표시할 본문 docJson (Step 1/2 와 동일 스타일) */
  backgroundDocJson?: any;
  /** 필드 입력 상태가 바뀔 때마다 진행률을 부모로 올려준다(계약서 밖 진행 체커용). */
  onProgressChange?: (progress: ContractProgress) => void;
  /** true 면 부모(flex-1) 높이를 꽉 채우고 스크롤은 계약 내용 안에서만(페이지 이중 스크롤 방지). */
  fillHeight?: boolean;
}

export type RenderedContractRendererRef = InlineContractRendererRef & {
  getProgress: () => ContractProgress;
  /** 특정 필드로 부드럽게 스크롤 + 하이라이트 */
  scrollToField: (key: string) => void;
  /** 아직 안 채운 첫 필드로 스크롤 */
  scrollToNextIncomplete: () => void;
  /** 미완료 항목을 순차로: 첫 항목으로 스크롤+입력 팝업 → 저장 시 다음 항목 팝업 자동 진행 */
  startGuidedInput: () => void;
};

const clampScale = (value: number): number =>
  Math.min(MAX_PAGE_SCALE, Math.max(MIN_PAGE_SCALE, value));

const touchDistance = (touches: React.TouchList): number => {
  if (touches.length < 2) return 0;
  const a = touches[0];
  const b = touches[1];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
};

const textValue = (v: string | null | undefined): string =>
  v != null && v !== "" ? String(v) : "";

const truthy = (value: string | null | undefined): boolean => {
  if (value == null) return false;
  const v = value.trim();
  return (
    v === "1" ||
    v.toUpperCase() === "Y" ||
    v.toUpperCase() === "TRUE" ||
    v.toUpperCase() === "ON" ||
    v.toUpperCase() === "CHECKED"
  );
};

const parseManifest = (raw?: string | null): PlacementManifest => {
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const fieldLabel = (type: string, label?: string, key?: string): string => {
  if (label && label.trim()) return label;
  if (type === "signature") return "보호자 서명";
  if (type === "seal") return "사업장 도장";
  if (type === "checkbox") return "동의";
  if (type === "date") return "날짜";
  if (type === "address") return "주소";
  return key || "입력";
};

const fieldColor = (type: string): string => {
  if (type === "signature") return "#F472B6";
  if (type === "seal") return "#C8102E";
  if (type === "checkbox") return "#22A55B";
  if (type === "date") return "#FF9934";
  if (type === "address") return "#0EA5E9";
  if (type === "text") return "#7D6DFF";
  return "#999";
};

const checkboxOptionsOf = (item: PlacementItem) => ({
  checkboxDisplay: item.fieldOptions?.checkboxDisplay ?? "BOX_AND_MARK",
  checkboxMode: item.fieldOptions?.checkboxMode ?? "SINGLE",
  groupKey: item.fieldOptions?.groupKey?.trim() || undefined,
  optionValue: item.fieldOptions?.optionValue?.trim() || item.label || item.key,
});

const isRequired = (item: PlacementItem): boolean => item.required !== false;
const isMultilineText = (item: PlacementItem): boolean =>
  item.fieldOptions?.textMode === "MULTILINE";

const RenderedContractRenderer = forwardRef<RenderedContractRendererRef, Props>(
  (
    {
      pageUrls,
      placementManifest,
      variables,
      initialAddress,
      sealImageUrl,
      sealImageUrls,
      signatureImageUrl,
      readOnly = false,
      className,
      backgroundDocJson,
      onProgressChange,
      fillHeight = false,
    },
    ref,
  ) => {
    const manifest = useMemo(
      () => parseManifest(placementManifest),
      [placementManifest],
    );
    const items = useMemo(() => manifest.items ?? [], [manifest]);
    const checkboxItems = useMemo(
      () =>
        items.filter(
          (item) => item.kind === "field" && item.fieldType === "checkbox",
        ),
      [items],
    );
    const pageWidth = manifest.pageWidth || DEFAULT_PAGE_WIDTH;
    const pageHeight = manifest.pageHeight || DEFAULT_PAGE_HEIGHT;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(pageWidth);
    const [fields, setFields] = useState<Record<string, string>>({});
    const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});
    const [signature, setSignature] = useState<string | null>(null);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [addrPickerKey, setAddrPickerKey] = useState<string | null>(null);
    const [manualScale, setManualScale] = useState<number | null>(null);
    const pinchRef = useRef<PinchState | null>(null);
    const pan = useDragPan(); // PC 마우스 왼쪽 드래그 = 손바닥 팬
    const [guided, setGuided] = useState(false); // 미완료 항목 순차 입력 가이드 진행 중 여부

    useEffect(() => {
      const el = rootRef.current;
      if (!el) return;
      const update = () => setContainerWidth(el.clientWidth || pageWidth);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }, [pageWidth]);

    useEffect(() => {
      const next: Record<string, string> = {};
      items
        .filter(
          (item) => item.kind === "variable" && ADDRESS_KEYS.has(item.key),
        )
        .forEach((item) => {
          const v = textValue(variables?.[item.key]) || initialAddress || "";
          if (v) next[item.key] = v;
        });
      if (Object.keys(next).length) {
        setFields((prev) => ({ ...next, ...prev }));
      }
    }, [initialAddress, items, variables]);

    const checkboxChecked = (item: PlacementItem): boolean =>
      checkboxes[item.key] ?? truthy(textValue(variables?.[item.key]));

    const checkboxGroupItems = (groupKey: string) =>
      checkboxItems.filter(
        (item) => checkboxOptionsOf(item).groupKey === groupKey,
      );

    const buildCheckboxOutput = (): Record<string, boolean> => {
      const output: Record<string, boolean> = {};
      checkboxItems.forEach((item) => {
        output[item.key] = checkboxChecked(item);
      });
      return output;
    };

    const buildFieldOutput = (): Record<string, string> => {
      const output: Record<string, string> = { ...fields };

      // 주소: 기본주소 + 상세주소를 합쳐 하나의 변수 값으로 만든다("도로명 ... 101동 202호").
      items
        .filter(
          (it) =>
            (it.kind === "variable" && ADDRESS_KEYS.has(it.key)) ||
            (it.kind === "field" && it.fieldType === "address"),
        )
        .forEach((it) => {
          const base = fields[it.key] || textValue(variables?.[it.key]) || "";
          const detail = fields[`${it.key}Detail`] || "";
          const full = [base, detail].filter(Boolean).join(" ");
          if (full) output[it.key] = full;
        });

      const groupValues = new Map<string, string[]>();

      checkboxItems.forEach((item) => {
        const options = checkboxOptionsOf(item);
        const checked = checkboxChecked(item);
        output[item.key] = checked ? "Y" : "N";
        if (options.groupKey && checked) {
          const values = groupValues.get(options.groupKey) ?? [];
          values.push(options.optionValue);
          groupValues.set(options.groupKey, values);
        }
      });

      groupValues.forEach((values, groupKey) => {
        output[groupKey] = values.join(",");
      });
      return output;
    };

    const toggleCheckbox = (item: PlacementItem) => {
      if (readOnly) return;
      const options = checkboxOptionsOf(item);
      setCheckboxes((prev) => {
        const current =
          prev[item.key] ?? truthy(textValue(variables?.[item.key]));
        const next = { ...prev };

        if (options.checkboxMode === "ONE_OF" && options.groupKey) {
          checkboxGroupItems(options.groupKey).forEach((groupItem) => {
            next[groupItem.key] = false;
          });
          if (!current || isRequired(item)) {
            next[item.key] = true;
          }
          return next;
        }

        next[item.key] = !current;
        return next;
      });
    };

    // 계약서 밖 진행 체커용 — 보호자가 실제로 채워야 하는 필드만 집계.
    // 자동채움 표시 변수(이름·전번 등)와 사업장 도장은 제외, 주소 변수와 서명/체크/입력 필드만 포함.
    const progress: ContractProgress = useMemo(() => {
      const seen = new Set<string>();
      const out: ContractProgressItem[] = [];
      for (const item of items) {
        let type: string | null = null;
        let done = false;
        if (item.kind === "field") {
          const ft = item.fieldType || "text";
          if (ft === "seal") continue;
          type = ft;
          if (ft === "signature") done = !!(signature || signatureImageUrl);
          else if (ft === "checkbox") done = checkboxChecked(item);
          else if (ft === "address")
            done = !!(fields[item.key] || textValue(variables?.[item.key]));
          else done = !!fields[item.key];
        } else if (item.kind === "variable" && ADDRESS_KEYS.has(item.key)) {
          type = "address";
          done = !!(fields[item.key] || textValue(variables?.[item.key]));
        } else {
          continue;
        }
        const dedup = `${type}:${item.key}`;
        if (seen.has(dedup)) continue;
        seen.add(dedup);
        out.push({
          key: item.key,
          type,
          label: fieldLabel(type, item.label, item.key),
          done,
        });
      }
      return {
        total: out.length,
        done: out.filter((i) => i.done).length,
        items: out,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, fields, checkboxes, signature, signatureImageUrl, variables]);

    useEffect(() => {
      onProgressChange?.(progress);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progress]);

    const scrollToField = (key: string) => {
      const el = rootRef.current?.querySelector<HTMLElement>(
        `[data-field-key="${key}"]`,
      );
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
        el.classList.add("contract-field-flash");
        window.setTimeout(
          () => el.classList.remove("contract-field-flash"),
          1400,
        );
      }
    };
    const scrollToNextIncomplete = () => {
      const next = progress.items.find((i) => !i.done);
      if (next) scrollToField(next.key);
    };

    // 순차 입력 가이드 — 미완료 항목의 입력 컨트롤을 하나씩 열어준다(계약서 위치로 이동하며).
    const openInputFor = (item: ContractProgressItem) => {
      scrollToField(item.key);
      if (item.type === "address") setAddrPickerKey(item.key);
      else if (item.type === "signature") setSignatureModalOpen(true);
      else setGuided(false); // 모달 없는 필드(체크/텍스트)는 스크롤만 하고 가이드 종료(무한루프 방지)
    };
    useEffect(() => {
      if (!guided) return;
      if (addrPickerKey || signatureModalOpen) return; // 팝업 열려 있으면 대기
      const next = progress.items.find((i) => !i.done);
      if (!next) {
        setGuided(false); // 다 채웠으면 종료
        return;
      }
      openInputFor(next);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guided, progress, addrPickerKey, signatureModalOpen]);

    useImperativeHandle(
      ref,
      () => ({
        getValues: (): InlineContractValues => ({
          fields: buildFieldOutput(),
          checkboxes: buildCheckboxOutput(),
          signature,
        }),
        getProgress: () => progress,
        scrollToField,
        scrollToNextIncomplete,
        startGuidedInput: () => setGuided(true),
        validate: () => {
          const validatedGroups = new Set<string>();
          for (const item of items) {
            if (
              item.kind === "variable" &&
              ADDRESS_KEYS.has(item.key) &&
              !fields[item.key] &&
              !variables?.[item.key]
            ) {
              return "주소를 입력해주세요.";
            }
            if (item.kind === "field" && item.fieldType === "checkbox") {
              const options = checkboxOptionsOf(item);
              if (options.groupKey && options.checkboxMode !== "SINGLE") {
                if (validatedGroups.has(options.groupKey)) continue;
                validatedGroups.add(options.groupKey);
                const group = checkboxGroupItems(options.groupKey);
                if (group.some(isRequired) && !group.some(checkboxChecked)) {
                  return `'${options.groupKey}' 항목을 선택해주세요.`;
                }
              } else if (isRequired(item) && !checkboxChecked(item)) {
                return `'${fieldLabel(item.fieldType, item.label, item.key)}' 항목에 체크해주세요.`;
              }
            }
            if (
              item.kind === "field" &&
              item.fieldType === "signature" &&
              !signature &&
              !signatureImageUrl
            ) {
              return "서명을 해주세요.";
            }
          }
          return null;
        },
      }),
      [
        checkboxItems,
        checkboxes,
        fields,
        items,
        signature,
        signatureImageUrl,
        variables,
      ],
    );

    const fitScale = clampScale(
      Math.min(1, Math.max(1, containerWidth - 8) / pageWidth),
    );
    const pageScale = manualScale ?? fitScale;
    const zoomPercent = Math.round(pageScale * 100);
    const zoomOut = () =>
      setManualScale((prev) => clampScale((prev ?? pageScale) - ZOOM_STEP));
    const zoomIn = () =>
      setManualScale((prev) => clampScale((prev ?? pageScale) + ZOOM_STEP));
    const resetZoom = () => setManualScale(null);

    // PC 마우스 휠 = 계약 내용 확대/축소 (모바일은 핀치). 휠 기본 스크롤은 막고 줌으로 사용.
    const fitScaleRef = useRef(fitScale);
    fitScaleRef.current = fitScale;
    useEffect(() => {
      const el = pan.ref.current;
      if (!el) return;
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.0012);
        setManualScale((prev) =>
          clampScale((prev ?? fitScaleRef.current) * factor),
        );
      };
      el.addEventListener("wheel", onWheel, { passive: false });
      return () => el.removeEventListener("wheel", onWheel);
    }, [pan.ref]);
    const setField = (key: string, value: string) =>
      setFields((prev) => ({ ...prev, [key]: value }));

    const startPinchZoom = (event: React.TouchEvent<HTMLDivElement>) => {
      if (event.touches.length !== 2) return;
      const distance = touchDistance(event.touches);
      if (distance <= 0) return;
      event.preventDefault();
      pinchRef.current = { startDistance: distance, startScale: pageScale };
    };

    const movePinchZoom = (event: React.TouchEvent<HTMLDivElement>) => {
      const pinch = pinchRef.current;
      if (!pinch || event.touches.length !== 2) return;
      const distance = touchDistance(event.touches);
      if (distance <= 0) return;
      event.preventDefault();
      setManualScale(
        clampScale(pinch.startScale * (distance / pinch.startDistance)),
      );
    };

    const endPinchZoom = (event: React.TouchEvent<HTMLDivElement>) => {
      if (event.touches.length < 2) {
        pinchRef.current = null;
      }
    };

    const renderAddressControl = (keyName: string, label?: string) => {
      const base =
        fields[keyName] ||
        textValue(variables?.[keyName]) ||
        initialAddress ||
        "";
      const detail = fields[`${keyName}Detail`] ?? "";
      const full = [base, detail].filter(Boolean).join(" ");
      if (readOnly) {
        return (
          <span
            className="font-medium text-[#111]"
            style={{ whiteSpace: "nowrap" }}
          >
            {full}
          </span>
        );
      }
      // 탭하면 주소검색+상세주소 입력 모달을 연다(작은 인라인 칸 대신).
      return (
        <button
          type="button"
          onClick={() => setAddrPickerKey(keyName)}
          className="inline-flex max-w-full items-center gap-1 rounded border border-[#FFB330] bg-[#FFF7E6] px-2 py-[3px] text-[12px] text-[#111]"
        >
          <span className="truncate text-left">
            {full || label || "주소 입력"}
          </span>
        </button>
      );
    };

    const renderVariable = (item: PlacementItem) => {
      // 주소만 보호자가 직접 입력(주소검색+상세주소). 그 외 변수는 전부 자동채움 표시 전용.
      if (ADDRESS_KEYS.has(item.key)) {
        return renderAddressControl(item.key, item.label);
      }
      // 자동채움 변수 = 표시 전용. 값이 있으면 값, 없으면 빈칸(라벨/키/입력칸을 절대 노출하지 않음).
      // 한 줄로 표시(줄바꿈 금지) — 슬롯보다 살짝 길어도 두 줄로 씹히지 않게(박스는 overflow visible).
      const value = textValue(variables?.[item.key]);
      return (
        <span
          className="font-medium text-[#111]"
          style={{ whiteSpace: "nowrap" }}
        >
          {value}
        </span>
      );
    };

    const renderField = (item: PlacementItem) => {
      const type = item.fieldType || "text";
      const label = fieldLabel(type, item.label, item.key);
      const color = fieldColor(type);
      if (type === "seal") {
        const imageUrl = sealImageUrls?.[item.key] || sealImageUrl;
        if (imageUrl) {
          return (
            <img
              src={imageUrl}
              alt={label}
              className="block h-full w-full object-contain"
            />
          );
        }
        return null;
      }
      if (type === "signature") {
        const image = signature || signatureImageUrl;
        return (
          <button
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && setSignatureModalOpen(true)}
            className="flex h-full w-full items-center justify-center bg-transparent p-0 text-[11px] font-semibold disabled:cursor-default"
            style={{ color }}
          >
            {image ? (
              <img
                src={image}
                alt={label}
                className="block h-full w-full object-contain"
              />
            ) : (
              <span
                className="rounded border border-dashed bg-white/70 px-2 py-1"
                style={{ borderColor: color }}
              >
                {label}
              </span>
            )}
          </button>
        );
      }
      if (type === "checkbox") {
        const checked = checkboxChecked(item);
        const options = checkboxOptionsOf(item);
        const showsBox = options.checkboxDisplay === "BOX_AND_MARK";
        return (
          <button
            type="button"
            disabled={readOnly}
            aria-label={label}
            title={label}
            onClick={() => toggleCheckbox(item)}
            className="flex h-full w-full items-center justify-center text-[#FF9934] disabled:cursor-default"
            style={{
              border: showsBox ? `1.5px solid ${color}` : "0",
              background: showsBox ? "#fff" : "transparent",
              fontSize: Math.max(12, Math.min(item.width, item.height) * 0.78),
              lineHeight: 1,
            }}
          >
            {checked ? "✓" : ""}
          </button>
        );
      }
      if (type === "address") {
        return renderAddressControl(item.key, label);
      }
      if (readOnly) {
        return (
          <span className="h-full w-full whitespace-pre-wrap break-words text-left text-[12px] leading-snug text-[#333]">
            {fields[item.key] || variables?.[item.key] || label}
          </span>
        );
      }
      if (isMultilineText(item)) {
        return (
          <textarea
            value={fields[item.key] ?? ""}
            onChange={(e) => setField(item.key, e.target.value)}
            placeholder={label}
            className="h-full w-full resize-none rounded border-2 border-dashed bg-white/70 px-1.5 py-1 text-[12px] leading-snug text-[#111] focus:outline-none"
            style={{ borderColor: color }}
          />
        );
      }
      return (
        <input
          type={type === "date" ? "date" : "text"}
          value={fields[item.key] ?? ""}
          onChange={(e) => setField(item.key, e.target.value)}
          placeholder={label}
          className="h-full w-full border-0 border-b-2 border-dashed bg-transparent px-1 text-[12px] focus:outline-none"
          style={{ borderColor: color, color: "#111" }}
        />
      );
    };

    const itemsByPage = (pageNo: number) =>
      items.filter((item) => item.pageNo === pageNo);

    return (
      <div
        ref={rootRef}
        className={
          fillHeight ? `flex min-h-0 flex-col ${className ?? ""}` : className
        }
      >
        {/* 편집기(.contract-page-canvas) 와 동일 CSS — 렌더 이미지 미생성 시 docJson 배경 표시용 */}
        <style>{CONTRACT_PAGE_CSS}</style>
        <style>{`
          @keyframes contractFieldFlash {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,153,52,0); }
            30% { box-shadow: 0 0 0 4px rgba(255,153,52,0.6); }
          }
          .contract-field-flash { animation: contractFieldFlash 1.4s ease-in-out; border-radius: 4px; }
          .contract-scroll-hide { scrollbar-width: none; -ms-overflow-style: none; }
          .contract-scroll-hide::-webkit-scrollbar { display: none; width: 0; height: 0; }
        `}</style>
        <div className="sticky top-0 z-20 mb-2 flex shrink-0 items-center justify-end gap-1 bg-white/95 py-1">
          <button
            type="button"
            onClick={zoomOut}
            disabled={pageScale <= MIN_PAGE_SCALE + 0.01}
            aria-label="축소"
            title="축소"
            className="flex h-8 w-8 items-center justify-center rounded border border-[#E9E9E9] bg-white text-[18px] font-semibold text-[#444] disabled:opacity-35"
          >
            −
          </button>
          <button
            type="button"
            onClick={resetZoom}
            aria-label="화면 맞춤"
            title="화면 맞춤"
            className="h-8 min-w-[58px] rounded border border-[#E9E9E9] bg-white px-2 text-[12px] font-semibold text-[#444]"
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={pageScale >= MAX_PAGE_SCALE - 0.01}
            aria-label="확대"
            title="확대"
            className="flex h-8 w-8 items-center justify-center rounded border border-[#E9E9E9] bg-white text-[18px] font-semibold text-[#444] disabled:opacity-35"
          >
            +
          </button>
        </div>

        <div
          ref={pan.ref}
          className={`contract-scroll-hide ${fillHeight ? "min-h-0 flex-1 pb-1" : "pb-1"}`}
          style={{
            maxHeight: fillHeight ? undefined : "70vh",
            overflow: "auto",
            cursor: "grab",
            overscrollBehavior: "contain",
          }}
          onMouseDown={pan.onMouseDown}
          onMouseMove={pan.onMouseMove}
          onMouseUp={pan.onMouseUp}
          onMouseLeave={pan.onMouseLeave}
          onTouchStart={startPinchZoom}
          onTouchMove={movePinchZoom}
          onTouchEnd={endPinchZoom}
          onTouchCancel={endPinchZoom}
        >
          {pageUrls.map((url, index) => {
            const pageNo = index + 1;
            return (
              <div
                key={`${url}-${pageNo}`}
                style={{
                  width: pageWidth * pageScale,
                  height: pageHeight * pageScale,
                  margin: "0 auto 12px",
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: pageWidth,
                    height: pageHeight,
                    minWidth: pageWidth,
                    minHeight: pageHeight,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    transform: `scale(${pageScale})`,
                    transformOrigin: "top left",
                  }}
                >
                  {url ? (
                    <img
                      src={url}
                      alt={`계약서 ${pageNo}페이지`}
                      draggable={false}
                      style={{
                        display: "block",
                        width: pageWidth,
                        height: pageHeight,
                        objectFit: "fill",
                        userSelect: "none",
                        pointerEvents: "none",
                      }}
                    />
                  ) : backgroundDocJson ? (
                    // 렌더 이미지 없지만 본문 docJson 있으면 배경으로 표시 (편집기와 동일 스타일)
                    renderPageBackground(backgroundDocJson, pageNo)
                  ) : (
                    <div
                      style={{
                        width: pageWidth,
                        height: pageHeight,
                        background: "#fff",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        paddingTop: 24,
                        color: "#BBB",
                        fontSize: 11,
                        pointerEvents: "none",
                      }}
                    >
                      페이지 {pageNo}
                    </div>
                  )}
                  {itemsByPage(pageNo).map((item, itemIndex) => {
                    // 자동채움 변수(주소 제외)는 값이 슬롯보다 길어도 한 줄로 표시하고 잘리지 않게(overflow visible).
                    // 입력 필드(서명/체크/도장)는 박스 안에 담아야 하므로 hidden 유지.
                    const isDisplayVar =
                      item.kind === "variable" && !ADDRESS_KEYS.has(item.key);
                    // 필드 필수/선택은 내부 설정값일 뿐 — 계약서 화면/PDF엔 표시하지 않는다.
                    return (
                      <div
                        key={`${item.kind}-${item.key}-${itemIndex}`}
                        data-field-key={item.key}
                        style={{
                          position: "absolute",
                          left: item.x,
                          top: item.y,
                          width: item.width,
                          height: item.height,
                          boxSizing: "border-box",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: isDisplayVar
                            ? "flex-start"
                            : "center",
                          overflow: isDisplayVar ? "visible" : "hidden",
                          fontSize: Math.max(
                            10,
                            Math.min(14, item.height * 0.72),
                          ),
                          lineHeight: 1.2,
                        }}
                      >
                        {item.kind === "field"
                          ? renderField(item)
                          : renderVariable(item)}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <AddressInputModal
          open={addrPickerKey != null}
          initialBase={
            addrPickerKey
              ? fields[addrPickerKey] ||
                textValue(variables?.[addrPickerKey]) ||
                ""
              : ""
          }
          initialDetail={
            addrPickerKey ? fields[`${addrPickerKey}Detail`] || "" : ""
          }
          onClose={() => {
            setAddrPickerKey(null);
            setGuided(false); // 주소 입력 취소 시 순차 가이드 종료
          }}
          onComplete={(result) => {
            if (!addrPickerKey) return;
            setField(addrPickerKey, result.address);
            setField(`${addrPickerKey}Detail`, result.detail);
            setField(`${addrPickerKey}_zonecode`, result.zonecode);
            setField(`${addrPickerKey}_roadAddress`, result.roadAddress);
            setField(`${addrPickerKey}_jibunAddress`, result.jibunAddress);
            if (result.buildingName)
              setField(`${addrPickerKey}_buildingName`, result.buildingName);
            setAddrPickerKey(null);
          }}
        />

        {signatureModalOpen && (
          <TransparentSignatureCaptureModal
            onClose={() => {
              setSignatureModalOpen(false);
              setGuided(false); // 서명 취소 시 순차 가이드 종료
            }}
            onSave={(dataUrl) => {
              setSignature(dataUrl);
              setSignatureModalOpen(false);
            }}
          />
        )}
      </div>
    );
  },
);

RenderedContractRenderer.displayName = "RenderedContractRenderer";

const TransparentSignatureCaptureModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (dataUrl: string) => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pointOf = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pointOf(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pointOf(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const save = () => {
    if (!hasDrawn) {
      alert("서명을 먼저 입력해 주세요.");
      return;
    }
    onSave(canvasRef.current!.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 md:items-center">
      <div className="w-full bg-white p-5 shadow-xl md:max-w-md md:rounded-2xl">
        <div className="mb-3 text-[16px] font-bold text-[#111]">서명</div>
        <canvas
          ref={canvasRef}
          width={600}
          height={240}
          className="h-auto w-full touch-none rounded-lg border border-[#E9E9E9] bg-white"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={() => {
            drawing.current = false;
          }}
          onMouseLeave={() => {
            drawing.current = false;
          }}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={() => {
            drawing.current = false;
          }}
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={clear}
            className="flex-1 rounded-full border border-[#E9E9E9] py-2.5 text-[12px] text-[#666]"
          >
            지우기
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-[#E9E9E9] py-2.5 text-[12px] text-[#666]"
          >
            취소
          </button>
          <button
            type="button"
            onClick={save}
            className="flex-[2] rounded-full bg-[#FF9934] py-2.5 text-[12px] font-semibold text-white"
          >
            서명 사용
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenderedContractRenderer;
